import assistantStore from '@stores/assistantStore'
import { observer } from 'mobx-react'
import { useId } from 'react'

import { useAssistantSession } from '../AssistantSessionContext'

import { useAssistantPlacement } from './PlacementContext'
import { placementQuestionText } from './questionText'

const LOCKED_HINT = 'Wait for K-Assist to finish answering'

/**
 * The placement's one-click questions, above the composer (placement.quickQuestions). While the
 * chat is empty they wrap under their label; once it has messages they keep to one scrolling row
 * (`when: 'always'`) or go away (`when: 'empty'`). Nothing for a placement without them.
 */
const PlacementQuickQuestions = () => {
  const session = useAssistantSession()
  const placement = useAssistantPlacement()
  const lockedHintId = useId()
  const config = placement.quickQuestions
  const empty = session.messages.length === 0
  // A pending clarification would take the question as its answer; nothing can be asked when
  // the assistant is not available, and these questions not without the placement's tools or
  // once the host has switched the placement off (the pane itself stays until it is closed).
  if (
    !config ||
    !config.items.length ||
    (config.when === 'empty' && !empty) ||
    assistantStore.unavailableReason !== null ||
    session.pendingClarification ||
    !assistantStore.canUsePlacement(placement)
  ) {
    return null
  }
  // Locked (an answer is coming, a confirm/cancel is being applied): still focusable, so a
  // keyboard or screen-reader user hears why they cannot be asked yet.
  const locked = session.isInputLocked
  const ask = (question: string) => {
    if (locked) {
      return
    }
    session.sendMessage(
      placementQuestionText(
        question,
        session.placementContext,
        session.activePageContext
      ),
      { commands: placement.composer.slashCommands }
    )
  }

  return (
    <div
      role="group"
      aria-label={config.label ?? 'Quick questions'}
      className={`flex flex-shrink-0 gap-[6px] px-[14px] pt-2 ${
        empty ? 'flex-wrap' : 'flex-nowrap overflow-x-auto pb-1'
      }`}
    >
      {empty && config.label && (
        <span className="w-full text-[11px] font-bold uppercase tracking-wider text-[#8190a4]">
          {config.label}
        </span>
      )}
      {config.items.map(question => (
        <button
          key={question}
          type="button"
          aria-disabled={locked || undefined}
          aria-describedby={locked ? lockedHintId : undefined}
          title={locked ? LOCKED_HINT : undefined}
          onClick={() => ask(question)}
          className={`shrink-0 rounded-[14px] border border-solid border-[#d2d8e2] bg-white px-[10px] py-[5px] text-xs font-semibold text-[#3d4756] ${
            locked
              ? 'cursor-not-allowed opacity-[.55]'
              : 'cursor-pointer hover:border-[#1fc0da] hover:bg-[#f4fbfd] hover:text-[#1f2733]'
          } ${empty ? 'whitespace-normal text-left' : 'whitespace-nowrap'}`}
          style={{ fontFamily: 'inherit' }}
        >
          {question}
        </button>
      ))}
      {locked && (
        <span id={lockedHintId} className="sr-only">
          {LOCKED_HINT}
        </span>
      )}
    </div>
  )
}

export default observer(PlacementQuickQuestions)
